import { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import GGLoaderButton from "../../../components/CommonComponents/GGLoaderButton/GGLoaderButton";
import OtpInputField from "../../../components/UtilityComponents/OTPInputField/OTPInputField";
import MobilePasswordField from "./MobilePasswordField";
import { usePasswordResetFlow } from "../../../components/PageComponents/AuthPageComponents/hooks/usePasswordResetFlow";
import { useRegistrationOtpVerification } from "../../../components/PageComponents/AuthPageComponents/hooks/useRegistrationOtpVerification";
import { AuthFlowType, OTP_LENGTH, PasswordResetStep } from "../../../enums/AuthFlowEnums";
import successLottie from "../../../assets/lottie_animations/success-lottie.json";
import loaderLottie from "../../../assets/lottie_animations/bubble-loader.json";

const PRIMARY_COLOR = "#EF4D00";

type MobileAuthFlowProps = {
    type: AuthFlowType;
    email: string;
    name: string;
    password: string;
    /** Bot-trap value captured on the registration form. */
    honeypot?: string;
    onClose: () => void;
    onPasswordResetComplete: () => void;
};

const MobileAuthFlow = (props: MobileAuthFlowProps) => (
    props.type === AuthFlowType.Registration
        ? <MobileRegistrationFlow {...props} />
        : <MobilePasswordResetFlow {...props} />
);

const MobileRegistrationFlow = ({ email, name, password, honeypot, onClose }: MobileAuthFlowProps) => {
    const navigate = useNavigate();
    const registration = useRegistrationOtpVerification({
        email,
        name,
        password,
        honeypot,
        onRegistered: () => navigate("/"),
    });

    if (registration.hasRegistered) {
        return (
            <FlowSuccess
                title="Verify your email"
                message={`Congratulations ${name} for becoming a part of GenieGig!`}
                footnote="Logging you in now..."
            />
        );
    }

    return (
        <OtpStep
            title="Verify your email"
            instruction="Please verify your email by entering the OTP sent to your email"
            note="Note: Please check your spam section as well for the verification email."
            email={email}
            enteredOtpLength={registration.otp.length}
            error={registration.error}
            isLoading={registration.isLoading}
            isResending={registration.isResending}
            canResend={registration.canResend}
            resendButtonText={registration.resendButtonText}
            onChangeOtp={registration.setOtp}
            onBack={onClose}
            onVerify={registration.verifyOtpAndRegister}
            onResend={registration.resendOtp}
        />
    );
};

const MobilePasswordResetFlow = ({ onClose, onPasswordResetComplete }: MobileAuthFlowProps) => {
    const flow = usePasswordResetFlow(onPasswordResetComplete);

    const submit = (action: () => void) => (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        action();
    };

    if (flow.step === PasswordResetStep.Success) {
        return (
            <FlowSuccess
                title="Forgot your password?"
                message="Successfully changed your password."
                footnote="Re-logging you in now..."
            />
        );
    }

    if (flow.step === PasswordResetStep.Email) {
        return (
            <form className="mobile-auth-flow" onSubmit={submit(flow.sendOtp)} noValidate>
                <h2>Forgot your password?</h2>
                <label className="mobile-auth-field">
                    <span className="sr-only">Email</span>
                    <input
                        type="email"
                        placeholder="Email"
                        autoComplete="email"
                        inputMode="email"
                        value={flow.email}
                        onChange={(event) => flow.setEmail(event.target.value)}
                    />
                </label>
                <FlowError error={flow.error} />
                <div className="mobile-auth-flow-actions">
                    <button type="button" className="mobile-auth-secondary-button" onClick={onClose}>Back</button>
                    <GGLoaderButton type="submit" isLoading={flow.isLoading} colorHex={PRIMARY_COLOR}>Send OTP</GGLoaderButton>
                </div>
            </form>
        );
    }

    if (flow.step === PasswordResetStep.NewPassword) {
        return (
            <form className="mobile-auth-flow" onSubmit={submit(flow.changePassword)} noValidate>
                <h2>Forgot your password?</h2>
                <p>Please choose a new password for your GenieGig account</p>
                <MobilePasswordField
                    placeholder="Enter new password"
                    value={flow.newPassword}
                    onChange={flow.setNewPassword}
                    autoComplete="new-password"
                />
                <MobilePasswordField
                    placeholder="Confirm new password"
                    value={flow.confirmPassword}
                    onChange={flow.setConfirmPassword}
                    autoComplete="new-password"
                />
                <FlowError error={flow.error} />
                <GGLoaderButton type="submit" fullWidth isLoading={flow.isLoading} colorHex={PRIMARY_COLOR}>
                    Change password and Login
                </GGLoaderButton>
            </form>
        );
    }

    return (
        <OtpStep
            title="Forgot your password?"
            instruction="Enter the 6 digit OTP sent to your email address"
            note="Note: Please check your spam section as well for the password reset mail."
            email={flow.email}
            enteredOtpLength={flow.otp.length}
            error={flow.error}
            isLoading={flow.isLoading}
            isResending={flow.isResending}
            canResend={flow.canResend}
            resendButtonText={flow.resendButtonText}
            onChangeOtp={flow.setOtp}
            onBack={flow.backToEmailStep}
            onVerify={flow.verifyOtp}
            onResend={flow.resendOtp}
        />
    );
};

type OtpStepProps = {
    title: string;
    instruction: string;
    note: string;
    email: string;
    enteredOtpLength: number;
    error: string;
    isLoading: boolean;
    isResending: boolean;
    canResend: boolean;
    resendButtonText: string;
    onChangeOtp: (otp: string) => void;
    onBack: () => void;
    onVerify: () => void;
    onResend: () => void;
};

const OtpStep = ({
    title, instruction, note, email, enteredOtpLength, error, isLoading, isResending,
    canResend, resendButtonText, onChangeOtp, onBack, onVerify, onResend,
}: OtpStepProps) => (
    <div className="mobile-auth-flow">
        <h2>{title}</h2>
        <p>{instruction}</p>
        <strong className="mobile-auth-flow-email">{email}</strong>
        <p className="mobile-auth-flow-note">{note}</p>
        <OtpInputField onChangeOtp={onChangeOtp} length={OTP_LENGTH} className="mobile-otp" />
        <FlowError error={error} />
        <div className="mobile-auth-flow-actions">
            <button type="button" className="mobile-auth-secondary-button" onClick={onBack}>Back</button>
            <GGLoaderButton
                type="button"
                isLoading={isLoading}
                disabled={enteredOtpLength !== OTP_LENGTH || isResending}
                colorHex={PRIMARY_COLOR}
                onClick={onVerify}
            >
                Verify OTP
            </GGLoaderButton>
        </div>
        {isResending
            ? <Lottie className="mobile-auth-resend-loader" animationData={loaderLottie} loop />
            : (
                <button type="button" className="mobile-auth-resend-button" disabled={!canResend} onClick={onResend}>
                    {resendButtonText}
                </button>
            )}
    </div>
);

const FlowSuccess = ({ title, message, footnote }: { title: string; message: string; footnote: string }) => (
    <div className="mobile-auth-flow mobile-auth-success" aria-live="polite">
        <h2>{title}</h2>
        <Lottie className="mobile-auth-success-animation" animationData={successLottie} loop />
        <p>{message}</p>
        <strong>{footnote}</strong>
    </div>
);

const FlowError = ({ error }: { error: string }) => <p className="mobile-auth-error" aria-live="polite">{error}</p>;

export default MobileAuthFlow;